import React, { useState, useRef, useEffect, useCallback } from 'react';
import useAudioAnalyzer from '../hooks/useAudioAnalyzer';

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function pickMimeType() {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

/**
 * LiveVideoRecorder
 * Camera + microphone recording with live preview.
 *
 * Props:
 *   onFileReady {func} — called with (file, durationSeconds) when recording is stopped
 *   onSubmit    {func} — called with (file, durationSeconds) to trigger analysis
 *   isLoading   {bool} — disables controls while parent is uploading
 */
export default function LiveVideoRecorder({ onFileReady, onSubmit, isLoading }) {
  const previewRef = useRef(null);
  const playbackRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioFeaturesRef = useRef(null);

  const [phase, setPhase] = useState('idle');
  const [duration, setDuration] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const [error, setError] = useState(null);

  const audioAnalyzer = useAudioAnalyzer();

  useEffect(() => {
    return () => {
      audioAnalyzer.cleanup();
      stopStream();
      clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (recordedBlob && playbackRef.current) {
      playbackRef.current.src = URL.createObjectURL(recordedBlob);
    }
  }, [recordedBlob]);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (previewRef.current) previewRef.current.srcObject = null;
  };

  const startRecording = async () => {
    setError(null);
    setDuration(0);
    setRecordedBlob(null);
    chunksRef.current = [];
    audioFeaturesRef.current = null;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });

      audioAnalyzer.attachStream(stream);
      streamRef.current = stream;
      if (previewRef.current) {
        previewRef.current.srcObject = stream;
        previewRef.current.play();
      }

      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const mime = mimeType || 'video/webm';
        const blob = new Blob(chunksRef.current, { type: mime });
        const ext = mime.includes('mp4') ? 'mp4' : 'webm';
        const file = new File([blob], `video-${Date.now()}.${ext}`, { type: mime });
        setRecordedBlob(blob);
        setRecordedDuration((prev) => prev);
        onFileReady?.(file, duration);
      };

      recorder.start(250);
      setPhase('recording');

      timerRef.current = setInterval(() => {
        setDuration((d) => d + 1);
        setRecordedDuration((d) => d + 1);
      }, 1000);

    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Camera/microphone permission denied. Please allow access and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera detected. Please connect a camera and try again.');
      } else {
        setError('Could not start recording. ' + err.message);
      }
    }
  };

  const pauseRecording = () => {
    if (phase === 'recording') {
      recorderRef.current?.pause();
      clearInterval(timerRef.current);
      setPhase('paused');
    } else if (phase === 'paused') {
      recorderRef.current?.resume();
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
      setPhase('recording');
    }
  };

  const stopRecording = () => {
    audioFeaturesRef.current = audioAnalyzer.stopAndGetMetrics();
    clearInterval(timerRef.current);
    recorderRef.current?.stop();
    stopStream();
    setPhase('done');
  };

  const handleDiscard = () => {
    setRecordedBlob(null);
    setDuration(0);
    setRecordedDuration(0);
    setPhase('idle');
  };

  const handleSubmit = useCallback(() => {
    if (!recordedBlob) return;
    const MAX_SIZE = 200 * 1024 * 1024;
    if (recordedBlob.size > MAX_SIZE) {
      const sizeMB = (recordedBlob.size / (1024 * 1024)).toFixed(1);
      setError(`Recording is too large (${sizeMB} MB). Maximum size is 200 MB. Try a shorter recording.`);
      return;
    }
    const mime = recordedBlob.type || 'video/webm';
    const ext = mime.includes('mp4') ? 'mp4' : 'webm';
    const file = new File([recordedBlob], `video-${Date.now()}.${ext}`, { type: mime });
    onSubmit?.(file, recordedDuration, audioFeaturesRef.current);
  }, [recordedBlob, recordedDuration, onSubmit]);

  const btnSecondary = {
    background: 'var(--bg-input)',
    border: '1px solid var(--border-subtle)',
    color: 'var(--text-secondary)',
    borderRadius: '0.5rem',
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  };

  return (
    <div className="space-y-4">
      {/* Error */}
      {error && (
        <div
          className="px-4 py-3 rounded-xl text-sm"
          style={{ color: 'var(--color-danger)', background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)' }}
        >
          {error}
        </div>
      )}

      {/* Live preview */}
      <div
        className={`relative rounded-xl overflow-hidden aspect-video ${phase === 'done' ? 'hidden' : ''}`}
        style={{ background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}
      >
        <video ref={previewRef} muted playsInline className="w-full h-full object-cover" />
        {phase === 'idle' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ color: 'var(--text-faint)' }}>
            <div className="text-5xl mb-3">📹</div>
            <p className="text-sm">Camera preview will appear here</p>
          </div>
        )}
        {(phase === 'recording' || phase === 'paused') && (
          <div
            className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1 rounded-full"
            style={{ background: 'rgba(0,0,0,0.6)' }}
          >
            <div className={`w-2.5 h-2.5 rounded-full ${phase === 'recording' ? 'animate-pulse' : ''}`} style={{ background: 'var(--color-danger)' }} />
            <span className="font-mono text-sm" style={{ color: 'var(--text-primary)' }}>{formatDuration(duration)}</span>
            {phase === 'paused' && (
              <span className="text-xs ml-1" style={{ color: 'var(--accent)' }}>PAUSED</span>
            )}
          </div>
        )}
      </div>

      {/* Playback */}
      {phase === 'done' && recordedBlob && (
        <div
          className="rounded-xl overflow-hidden aspect-video"
          style={{ background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)' }}
        >
          <video ref={playbackRef} controls playsInline className="w-full h-full object-cover" />
        </div>
      )}

      {/* Controls */}
      <div className="flex justify-center gap-3 flex-wrap">
        {phase === 'idle' && (
          <button onClick={startRecording} className="btn-bouncy text-sm px-6 py-2.5">
            📹 Start Recording
          </button>
        )}

        {(phase === 'recording' || phase === 'paused') && (
          <>
            <button onClick={pauseRecording} style={btnSecondary}>
              {phase === 'paused' ? '▶ Resume' : '⏸ Pause'}
            </button>
            <button
              onClick={stopRecording}
              className="text-sm font-medium px-5 py-2.5 rounded-lg"
              style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger)' }}
            >
              ⏹ Stop
            </button>
          </>
        )}

        {phase === 'done' && (
          <>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="btn-bouncy text-sm px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Analyzing…' : '🔍 Analyze Video'}
            </button>
            <button onClick={handleDiscard} disabled={isLoading} style={{ ...btnSecondary, opacity: isLoading ? 0.5 : 1 }}>
              Record Again
            </button>
          </>
        )}
      </div>

      {/* Tips */}
      <div
        className="rounded-xl p-3 text-sm"
        style={{ background: 'var(--color-info-dim)', border: '1px solid var(--color-info-border)', color: 'var(--text-secondary)' }}
      >
        <strong style={{ color: 'var(--color-info)' }}>Tips:</strong> Good lighting + quiet room = better analysis. Speak directly to the camera.
      </div>
    </div>
  );
}
